import allWords from 'an-array-of-english-words';
import { applyFlipAnimation, applyFoundAnimation } from './animationHelper';
import { useGameStore } from '../../store/gameStore';
import { getBonusLetter } from './nextLetterHelper';
import { playSound } from '../utils/soundEffectHelper';

export const pointMap: { [key: string]: number } = {
  a: 1,
  b: 3,
  c: 3,
  d: 2,
  e: 1,
  f: 4,
  g: 2,
  h: 4,
  i: 1,
  j: 8,
  k: 5,
  l: 1,
  m: 3,
  n: 1,
  o: 1,
  p: 3,
  q: 10,
  r: 1,
  s: 1,
  t: 1,
  u: 2,
  v: 4,
  w: 4,
  x: 8,
  y: 4,
  z: 10,
};

export const getWords = (boardSize: number) => {
  return allWords.filter((word: string) => word.length === boardSize);
};

const handleSounds = (foundWords: string[]) => {
  if (foundWords.length === 0) playSound('tileClick');
  else if (foundWords.includes('meow')) playSound('meow');
  else if (foundWords.includes('neigh')) playSound('neigh');
  else playSound('found');
};

const updateScore = (foundWords: string[], foundDiagonal: boolean) => {
  const bonusLetter = getBonusLetter().toLowerCase();
  let points: number = 0;
  foundWords.forEach((word) => {
    word.split('').forEach((letter) => {
      points += letter === bonusLetter ? pointMap[letter] * 2 : pointMap[letter];
    });
  });

  // Diagonal bonus
  if (foundDiagonal) points += 3;
  // Multi-word bonus
  points = points * foundWords.length;

  useGameStore.getState().addPoints(points);
};

// Time to wait to for tile flip after word(s) are found
export const foundWordDelay: number = 1400;

// Create deep copy of board for to avoid direct state mutation
export const cloneBoard = (board: string[][]) => {
  return board.map((row) => [...row]);
};

/**
 * Checks for duplicate found words
 */
const isDuplicate = (word: string) => {
  const mode = useGameStore.getState().mode;
  return useGameStore.getState().gameData[mode].foundWords.some((foundWord) => foundWord === word);
};

/**
 * Takes 2d array of tile IDs and removes them from the board
 * @param board
 * @param idArr
 */
function clearFoundWords(board: string[][], idArr: string[][]) {
  const newBoard = cloneBoard(board);

  idArr.forEach((word) => {
    word.forEach((tileId, i) => {
      const row = parseInt(tileId.substring(0, 1));
      const col = parseInt(tileId.substring(2));
      newBoard[row][col] = '';
      const tile = document.getElementById(tileId);
      setTimeout(() => {
        applyFoundAnimation(tile, i);
      }, 130 * i);
      setTimeout(() => {
        applyFlipAnimation(tile, { isWordFound: true });
      }, foundWordDelay - 200);
    });
  });

  setTimeout(() => {
    if (!useGameStore.getState().wasReset) {
      useGameStore.getState().updateBoard(newBoard);
    }
  }, foundWordDelay);
}

/**
 * Checks for words in every direction given the current tile clicked
 * @param board
 * @param row
 * @param col
 * @returns boolean based on found words array length
 */
export function checkForWords(board: string[][], row: number, col: number): boolean {
  const boardSize = board.length;
  const words = getWords(boardSize);

  const foundWords: string[] = [];
  const foundWordsIds: string[][] = [];

  let foundDiagonal: boolean = false;

  // Makes sure word exists and is not a duplicate of a previously found word
  const isValidWord = (arr: string[]) => {
    if (arr.length !== boardSize) return false;
    const word = arr.join('').toLowerCase();
    return words.includes(word) && !isDuplicate(word);
  };

  // Function to check both forward and backward directions
  const checkBothDirections = (arr: string[], idArr: string[], isDiagonal?: boolean) => {
    if (isValidWord(arr)) {
      foundWords.push(arr.join('').toLowerCase());
      foundWordsIds.push(idArr);
      if (isDiagonal) foundDiagonal = true;
    } else if (isValidWord([...arr].reverse())) {
      foundWords.push([...arr].reverse().join('').toLowerCase());
      foundWordsIds.push(idArr.reverse());
      if (isDiagonal) foundDiagonal = true;
    }
  };

  // Check Row (left-to-right and right-to-left)
  const rowWord = board[row];
  const rowWordIds = board[row].map((_, i) => `${row}-${i}`);
  checkBothDirections(rowWord, rowWordIds);

  // Check Column (top-to-bottom and bottom-to-top)
  const colWord = board.map((r) => r[col]);
  const colWordIds = board.map((r) => r[col]).map((_, i) => `${i}-${col}`);
  checkBothDirections(colWord, colWordIds);

  // Check Diagonal ↘ (Top-left to bottom-right)
  let diag1: string[] = [];
  let diag1Ids: string[] = [];
  for (let i = 0; i < boardSize; i++) {
    const newRow = row - col + i;
    if (newRow >= 0 && newRow < boardSize) {
      diag1.push(board[newRow][i]);
      diag1Ids.push(`${newRow}-${i}`);
    }
  }
  checkBothDirections(diag1, diag1Ids, true);

  // Check Diagonal ↙ (Bottom-left to top-right)
  let diag2: string[] = [];
  let diag2Ids: string[] = [];
  for (let i = 0; i < boardSize; i++) {
    const newRow = row + col - i;
    if (newRow >= 0 && newRow < boardSize) {
      diag2.push(board[newRow][i]);
      diag2Ids.push(`${newRow}-${i}`);
    }
  }
  checkBothDirections(diag2, diag2Ids, true);

  const isWordFound = foundWords.length > 0;
  handleSounds(foundWords);
  if (isWordFound) {
    clearFoundWords(board, foundWordsIds);
    updateScore(foundWords, foundDiagonal);
    useGameStore.getState().addFoundWords(foundWords);
    useGameStore.getState().setRecentFoundWords(foundWords);
  }

  return isWordFound;
}
